// Bausteine, die Upgrade- und Limit-Dialog teilen. Kopf, Admin-Hinweis,
// Termin- und Vergleichslink und die Bestätigung nach einer Anfrage.
//
// Keine Preise. Pakete vergleichen verlinkt die Preisseite der Landingpage.
// Paketnamen nur bei Kunden mit Plan-Payment oder in der Simulation, das
// prüft der Aufrufer über entitlements.plan.
import { DialogTitle } from '@headlessui/react'
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../constants/track-events'
import { PRICING_REQUEST_STATUS } from '../../features/pricing/pricing-slack'
import { getPricingCompareUrl } from '../../features/pricing/use-feature-gate'
import {
  AIVY_MEETING_URL,
  classNames,
  getAivyOsCustomerUrl
} from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import InfoCallout from '../tailwind/InfoCallout'

/** Paketname aus i18n, custom heißt „Individuell" */
export const planDisplayName = (t, plan) => {
  const id = typeof plan === 'string' ? plan : plan?.id
  return id ? t(`pricing.plans.${id}`) : null
}

const DialogCloseButton = ({ onClose }) => {
  const { t } = useTranslation()

  return (
    <button
      type='button'
      onClick={onClose}
      aria-label={t('pricing.dialog.close_action')}
      className='-mr-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600'
    >
      <XMarkIcon className='size-5' aria-hidden='true' />
    </button>
  )
}

DialogCloseButton.propTypes = { onClose: PropTypes.func.isRequired }

/** Icon-Kachel links, Schließen rechts */
export const DialogIconRow = ({ icon: Icon, onClose }) => (
  <div className='flex items-start justify-between gap-4'>
    <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-100'>
      <Icon className='size-6 text-gray-500' aria-hidden='true' />
    </div>
    <DialogCloseButton onClose={onClose} />
  </div>
)

DialogIconRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  onClose: PropTypes.func.isRequired
}

/** Nur für Aivy-Admins, Kunden sehen das nie */
export const AdminHint = ({ partnerId }) => {
  const { t } = useTranslation()

  return (
    <InfoCallout
      tone='neutral'
      action={
        partnerId ? (
          <a
            href={getAivyOsCustomerUrl({ partner_id: partnerId })}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-x-1 whitespace-nowrap font-semibold text-[#1660F3] hover:text-primaryBlue/70'
          >
            {t('pricing.dialog.admin_os_action')}
            <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
          </a>
        ) : undefined
      }
    >
      {t('pricing.dialog.admin_hint')}
    </InfoCallout>
  )
}

AdminHint.propTypes = { partnerId: PropTypes.string }
AdminHint.defaultProps = { partnerId: null }

/** „Termin buchen" als Link im Stil von SecondaryLG */
export const MeetingLink = ({ tracking, className }) => {
  const { t } = useTranslation()

  return (
    <a
      href={AIVY_MEETING_URL}
      target='_blank'
      rel='noopener noreferrer'
      onClick={() => trackEvent(trackEvents.PRICING_MEETING_PRESSED, tracking)}
      className={classNames(
        'inline-flex items-center justify-center whitespace-nowrap rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50',
        className
      )}
    >
      {t('pricing.dialog.meeting_action')}
    </a>
  )
}

MeetingLink.propTypes = {
  tracking: PropTypes.object,
  className: PropTypes.string
}

MeetingLink.defaultProps = { tracking: {}, className: undefined }

/** „Pakete vergleichen" als Textlink auf die Preisseite */
export const CompareLink = ({ className }) => {
  const { t, i18n } = useTranslation()

  return (
    <a
      href={getPricingCompareUrl(i18n.language)}
      target='_blank'
      rel='noopener noreferrer'
      className={classNames(
        'inline-flex items-center gap-x-1 whitespace-nowrap text-sm font-semibold text-[#1660F3] hover:text-primaryBlue/70',
        className
      )}
    >
      {t('pricing.lock.compare_link')}
      <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
    </a>
  )
}

CompareLink.propTypes = { className: PropTypes.string }
CompareLink.defaultProps = { className: undefined }

export const DialogFooter = ({ children, className }) => (
  <div
    className={classNames(
      'flex flex-col-reverse gap-3 rounded-b-xl border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between',
      className
    )}
  >
    {children}
  </div>
)

DialogFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}

DialogFooter.defaultProps = { className: undefined }

// PRICING_REQUEST_STATUS kommt aus pricing-slack.js, nicht aus
// use-pricing-request.js. Der Hook liegt im Import-Zyklus mit
// context/index.js, pricing-slack.js nicht.
const RESULT_COPY = {
  [PRICING_REQUEST_STATUS.SENT]: {
    title: 'pricing.dialog.sent_title',
    text: 'pricing.dialog.sent_text'
  },
  [PRICING_REQUEST_STATUS.DUPLICATE]: {
    title: 'pricing.dialog.already_sent_title',
    text: 'pricing.dialog.already_sent_text'
  },
  [PRICING_REQUEST_STATUS.DRY_RUN]: {
    title: 'pricing.dialog.admin_dry_run_title',
    text: 'pricing.dialog.admin_dry_run_text'
  }
}

/**
 * Bestätigung nach einer Anfrage aus dem Dialog (sent, duplicate, dry_run).
 * Ersetzt den Inhalt, nur „Schließen" bleibt und bekommt den Fokus, denn der
 * Button, der eben noch fokussiert war, ist weg.
 *
 * Eine Live-Region, die schon gefüllt eingehängt wird, lesen NVDA und JAWS
 * nicht zuverlässig vor. Das Ergebnis hängt deshalb per aria-describedby am
 * fokussierten Button und wird mit ihm angesagt.
 */
export const RequestResult = ({ status, onClose }) => {
  const { t } = useTranslation()
  const closeRef = useRef(null)
  const titleId = useId()
  const textId = useId()
  const copy = RESULT_COPY[status] || RESULT_COPY[PRICING_REQUEST_STATUS.SENT]

  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  return (
    <>
      <div className='px-6 pb-6 pt-6'>
        <div className='flex size-10 items-center justify-center rounded-full bg-green-50'>
          <CheckCircleIcon
            className='size-6 text-green-600'
            aria-hidden='true'
          />
        </div>
        <DialogTitle
          id={titleId}
          className='mt-4 text-lg font-semibold text-gray-900'
        >
          {t(copy.title)}
        </DialogTitle>
        <p id={textId} className='mt-2 text-sm text-gray-600'>
          {t(copy.text)}
        </p>
      </div>
      <DialogFooter className='sm:justify-end'>
        <button
          ref={closeRef}
          type='button'
          onClick={onClose}
          aria-describedby={`${titleId} ${textId}`}
          className='relative min-w-24 whitespace-nowrap rounded-full bg-primaryBlue px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
        >
          {t('pricing.dialog.close_action')}
        </button>
      </DialogFooter>
    </>
  )
}

RequestResult.propTypes = {
  status: PropTypes.oneOf(Object.keys(RESULT_COPY)).isRequired,
  onClose: PropTypes.func.isRequired
}
